// The Google login handoff: before leaving for Google, the browser keeps a
// random verifier in sessionStorage and sends only its SHA-256 challenge. The
// API returns a one-time code in the URL fragment, and the LinkedScope JWT is
// released only to the tab that holds the matching verifier.

export const GOOGLE_LOGIN_VERIFIER_STORAGE_KEY = "linkedscope_google_login_verifier";

export const GOOGLE_AUTH_RETURN_STATUS = {
  NONE: "none",
  CODE: "code",
  ERROR: "error",
};

const HANDOFF_CODE_PATTERN = /^[A-Za-z0-9_-]{43}$/;

export const toBase64Url = (bytes) => {
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

export const createHandoffVerifier = (cryptoApi = window.crypto) =>
  toBase64Url(cryptoApi.getRandomValues(new Uint8Array(32)));

export const buildHandoffChallenge = async (verifier, cryptoApi = window.crypto) => {
  const digest = await cryptoApi.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return toBase64Url(new Uint8Array(digest));
};

export const storeHandoffVerifier = (verifier, storage = window.sessionStorage) => {
  storage.setItem(GOOGLE_LOGIN_VERIFIER_STORAGE_KEY, verifier);
};

/** Reads and removes the verifier, so it can only be used once. */
export const takeHandoffVerifier = (storage = window.sessionStorage) => {
  try {
    const verifier = storage.getItem(GOOGLE_LOGIN_VERIFIER_STORAGE_KEY);
    storage.removeItem(GOOGLE_LOGIN_VERIFIER_STORAGE_KEY);
    return verifier || null;
  } catch {
    return null;
  }
};

/**
 * Reads the Google login return from the current location and returns the URL
 * without its Google parameters, so the caller can replace it immediately.
 */
export const readGoogleAuthReturn = ({ pathname = "", search = "", hash = "" } = {}) => {
  const hashParams = new URLSearchParams(String(hash).replace(/^#/, ""));
  const searchParams = new URLSearchParams(search);
  const handoffCode = hashParams.get("googleAuthCode");
  const isError = searchParams.get("googleAuth") === "error";

  if (!hashParams.has("googleAuthCode") && !isError) {
    return { status: GOOGLE_AUTH_RETURN_STATUS.NONE, code: null, errorCode: null, cleanedUrl: null };
  }

  hashParams.delete("googleAuthCode");
  ["googleAuth", "code", "message", "mode"].forEach((key) => searchParams.delete(key));
  const nextSearch = searchParams.toString();
  const nextHash = hashParams.toString();
  const cleanedUrl = `${pathname}${nextSearch ? `?${nextSearch}` : ""}${nextHash ? `#${nextHash}` : ""}`;

  if (handoffCode && HANDOFF_CODE_PATTERN.test(handoffCode) && !isError) {
    return { status: GOOGLE_AUTH_RETURN_STATUS.CODE, code: handoffCode, errorCode: null, cleanedUrl };
  }
  return {
    status: GOOGLE_AUTH_RETURN_STATUS.ERROR,
    code: null,
    errorCode: isError ? new URLSearchParams(search).get("code") : "GOOGLE_LOGIN_HANDOFF_INVALID",
    cleanedUrl,
  };
};
